import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { RoleBadge } from "@/components/Badges";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { UserPlus, Trash2, Pencil, Upload, Download, Shield, ShieldCheck, FileText } from "lucide-react";

const ROLES = [
  { value: "admin", label: "Admin" },
  { value: "project_manager", label: "Project Manager" },
  { value: "coordinator", label: "Country Coordinator" },
  { value: "viewer", label: "Viewer" },
];

const MENU_ITEMS = [
  { key: "overview", label: "Overview" },
  { key: "regions", label: "Regions" },
  { key: "projects", label: "Projects" },
  { key: "languages", label: "Languages" },
  { key: "timeline", label: "Timeline" },
  { key: "clusters", label: "Clusters" },
  { key: "notifications", label: "Notifications" },
];

const HUB_OPTIONS = ["NILDAR", "HILDAR", "WILDAR"];

export default function Users() {
  const [users, setUsers] = useState([]);
  const [allCountries, setAllCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    name: "", email: "", password: "", role: "viewer",
    menu_permissions: MENU_ITEMS.map((m) => m.key),
    country_permissions: [],
    hub_permissions: [],
    can_edit: true,
  });
  const [saving, setSaving] = useState(false);
  const [csvOpen, setCsvOpen] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [csvResult, setCsvResult] = useState(null);
  const [csvUploading, setCsvUploading] = useState(false);

  const load = () => api.get("/users").then((r) => setUsers(r.data)).finally(() => setLoading(false));
  useEffect(() => {
    load();
    api.get("/admin/all-countries").then((r) => setAllCountries(r.data)).catch(() => {});
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({
      name: "", email: "", password: "", role: "viewer",
      menu_permissions: MENU_ITEMS.map((m) => m.key),
      country_permissions: [],
      hub_permissions: [],
      can_edit: true,
    });
    setOpen(true);
  };
  const openEdit = (u) => {
    setEditing(u);
    setForm({
      name: u.name, email: u.email, password: "", role: u.role,
      menu_permissions: u.menu_permissions || MENU_ITEMS.map((m) => m.key),
      country_permissions: u.country_permissions || [],
      hub_permissions: u.hub_permissions || u.cluster_permissions || [],
      can_edit: u.can_edit !== false,
    });
    setOpen(true);
  };

  const toggleMenu = (k) => setForm((f) => ({
    ...f,
    menu_permissions: f.menu_permissions.includes(k)
      ? f.menu_permissions.filter((x) => x !== k)
      : [...f.menu_permissions, k],
  }));
  const toggleCountry = (c) => setForm((f) => ({
    ...f,
    country_permissions: f.country_permissions.includes(c)
      ? f.country_permissions.filter((x) => x !== c)
      : [...f.country_permissions, c],
  }));
  const toggleHub = (c) => setForm((f) => ({
    ...f,
    hub_permissions: f.hub_permissions.includes(c)
      ? f.hub_permissions.filter((x) => x !== c)
      : [...f.hub_permissions, c],
  }));

  const save = async () => {
    setSaving(true);
    try {
      if (editing) {
        const payload = {
          name: form.name, role: form.role,
          menu_permissions: form.menu_permissions,
          country_permissions: form.country_permissions,
          hub_permissions: form.hub_permissions,
          can_edit: form.can_edit,
        };
        if (form.password) payload.password = form.password;
        await api.patch(`/users/${editing.id}`, payload);
        toast.success("User updated");
      } else {
        await api.post("/users", form);
        toast.success("User created");
      }
      setOpen(false);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    } finally { setSaving(false); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete ${u.email}?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("User deleted");
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    }
  };

  return (
    <div className="max-w-[1200px] mx-auto px-10 py-10 fade-in-up" data-testid="users-page">
      <div className="flex justify-between items-end mb-8 flex-wrap gap-4">
        <div>
          <div className="mono text-[10px] uppercase tracking-widest text-[color:var(--ink-2)] mb-2">
            Access Control
          </div>
          <h1 className="serif text-5xl tracking-tight leading-none">Users</h1>
        </div>
        <div className="flex gap-2 items-center">
          <a
            href="data:text/csv;charset=utf-8,name,email,password,role,menu_permissions,country_permissions,can_edit%0AJane%20Coordinator,jane@example.org,Welcome%402026,coordinator,overview;regions;projects;languages,Bangladesh;Nigeria,yes%0AJohn%20Viewer,john@example.org,,viewer,overview;regions,,no%0A"
            download="users_template.csv"
            className="btn-ghost text-sm flex items-center gap-2"
            data-testid="csv-template"
          >
            <Download size={14} /> Template
          </a>
          <button
            className="btn-ghost text-sm flex items-center gap-2"
            onClick={() => { setCsvFile(null); setCsvResult(null); setCsvOpen(true); }}
            data-testid="import-csv-btn"
          >
            <Upload size={14} /> Import CSV
          </button>
          <button
            className="btn-primary flex items-center gap-2 text-sm"
            onClick={openCreate}
            data-testid="add-user-btn"
          >
            <UserPlus size={14} /> Add user
          </button>
        </div>
      </div>

      <div className="hairline bg-white overflow-hidden">
        <table className="w-full data-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Access</th>
              <th className="text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} data-testid={`user-row-${u.email}`}>
                <td className="font-medium">{u.name}</td>
                <td className="mono text-xs">{u.email}</td>
                <td><RoleBadge role={u.role} /></td>
                <td>
                  {u.role === "admin" ? (
                    <span className="chip chip-behind flex items-center gap-1 w-fit">
                      <ShieldCheck size={10} /> Full access
                    </span>
                  ) : u.can_edit ? (
                    <span className="chip chip-ontrack flex items-center gap-1 w-fit">
                      <ShieldCheck size={10} /> Edit
                    </span>
                  ) : (
                    <span className="chip flex items-center gap-1 w-fit">
                      <Shield size={10} /> Read-only
                    </span>
                  )}
                </td>
                <td className="text-right">
                  <button className="btn-ghost text-xs mr-2" onClick={() => openEdit(u)} data-testid={`edit-user-${u.email}`}>
                    <Pencil size={12} className="inline mr-1" /> Edit
                  </button>
                  <button
                    className="btn-ghost text-xs"
                    style={{ color: "var(--behind)" }}
                    onClick={() => remove(u)}
                    data-testid={`delete-user-${u.email}`}
                  >
                    <Trash2 size={12} className="inline mr-1" /> Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="user-dialog" className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="serif text-3xl">{editing ? "Edit User" : "New User"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="mono text-[10px] uppercase tracking-widest block mb-1">Name</label>
                <input className="input-flat" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-form-name" />
              </div>
              <div>
                <label className="mono text-[10px] uppercase tracking-widest block mb-1">Role</label>
                <select className="input-flat" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="user-form-role">
                  {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="mono text-[10px] uppercase tracking-widest block mb-1">Email</label>
                <input
                  type="email"
                  className="input-flat"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  disabled={!!editing}
                  data-testid="user-form-email"
                />
              </div>
              <div>
                <label className="mono text-[10px] uppercase tracking-widest block mb-1">
                  Password {editing && <span className="normal-case text-[color:var(--ink-3)]">(leave blank to keep)</span>}
                </label>
                <input
                  type="password"
                  className="input-flat"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  data-testid="user-form-password"
                />
              </div>
            </div>

            {/* Menu permissions - not applicable to admin */}
            {form.role !== "admin" && (
              <div className="border-t border-[color:var(--border)] pt-4">
                <div className="mono text-[10px] uppercase tracking-widest block mb-2">
                  Menu Access <span className="normal-case text-[color:var(--ink-3)]">(what this user can see in the sidebar)</span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {MENU_ITEMS.map((m) => {
                    const checked = form.menu_permissions.includes(m.key);
                    return (
                      <label
                        key={m.key}
                        className={`flex items-center gap-2 px-2 py-1 cursor-pointer text-sm border ${checked ? "border-[color:var(--ink)] bg-[color:var(--surface-2)]" : "border-transparent hover:border-[color:var(--border)]"}`}
                        data-testid={`menu-${m.key}`}
                      >
                        <input
                          type="checkbox"
                          className="accent-[color:var(--ink)]"
                          checked={checked}
                          onChange={() => toggleMenu(m.key)}
                        />
                        {m.label}
                      </label>
                    );
                  })}
                </div>
                <div className="mono text-[10px] text-[color:var(--ink-3)] mt-2">
                  Always admin-only: Upload and Download · Options · Users · Audit Log · Annotations Trail
                </div>
              </div>
            )}

            {/* Read-only vs edit toggle - not applicable to admin */}
            {form.role !== "admin" && (
              <div className="border-t border-[color:var(--border)] pt-4">
                <div className="mono text-[10px] uppercase tracking-widest block mb-2">Data Access Level</div>
                <div className="grid grid-cols-2 gap-2">
                  <label className={`flex items-center gap-2 px-3 py-2 border cursor-pointer ${!form.can_edit ? "border-[color:var(--ink)] bg-[color:var(--surface-2)]" : "border-[color:var(--border)] hover:border-[color:var(--border-strong)]"}`} data-testid="access-read">
                    <input type="radio" checked={!form.can_edit} onChange={() => setForm({ ...form, can_edit: false })} className="accent-[color:var(--ink)]" />
                    <div>
                      <div className="text-sm font-medium flex items-center gap-1"><Shield size={12} /> Read-only</div>
                      <div className="mono text-[10px] text-[color:var(--ink-3)]">Can view data but cannot make changes</div>
                    </div>
                  </label>
                  <label className={`flex items-center gap-2 px-3 py-2 border cursor-pointer ${form.can_edit ? "border-[color:var(--ink)] bg-[color:var(--surface-2)]" : "border-[color:var(--border)] hover:border-[color:var(--border-strong)]"}`} data-testid="access-edit">
                    <input type="radio" checked={!!form.can_edit} onChange={() => setForm({ ...form, can_edit: true })} className="accent-[color:var(--ink)]" />
                    <div>
                      <div className="text-sm font-medium flex items-center gap-1"><ShieldCheck size={12} /> Edit</div>
                      <div className="mono text-[10px] text-[color:var(--ink-3)]">Can add notes, concerns, update progress</div>
                    </div>
                  </label>
                </div>
              </div>
            )}

            {/* Country permissions - not applicable to admin */}
            {form.role !== "admin" && (
              <div className="border-t border-[color:var(--border)] pt-4">
                <div className="flex items-baseline justify-between mb-2">
                  <div className="mono text-[10px] uppercase tracking-widest">
                    Country Access <span className="normal-case text-[color:var(--ink-3)]">(leave empty to grant all countries)</span>
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      className="mono text-[10px] uppercase tracking-widest text-[color:var(--ink-2)] hover:text-[color:var(--ink)]"
                      onClick={() => setForm((f) => ({ ...f, country_permissions: allCountries }))}
                    >
                      All
                    </button>
                    <span className="text-[color:var(--ink-3)]">·</span>
                    <button
                      type="button"
                      className="mono text-[10px] uppercase tracking-widest text-[color:var(--ink-2)] hover:text-[color:var(--ink)]"
                      onClick={() => setForm((f) => ({ ...f, country_permissions: [] }))}
                    >
                      Clear
                    </button>
                  </div>
                </div>
                <div className="border border-[color:var(--border-strong)] max-h-56 overflow-y-auto p-3 bg-[color:var(--surface)] grid grid-cols-2 md:grid-cols-3 gap-1.5" data-testid="country-perms">
                  {allCountries.map((c) => {
                    const checked = form.country_permissions.includes(c);
                    return (
                      <label
                        key={c}
                        className={`flex items-center gap-2 px-2 py-1 cursor-pointer text-sm border ${checked ? "border-[color:var(--ink)] bg-[color:var(--surface-2)]" : "border-transparent hover:border-[color:var(--border)]"}`}
                        data-testid={`country-perm-${c}`}
                      >
                        <input
                          type="checkbox"
                          className="accent-[color:var(--ink)]"
                          checked={checked}
                          onChange={() => toggleCountry(c)}
                        />
                        <span className="truncate">{c}</span>
                      </label>
                    );
                  })}
                </div>
                <div className="mono text-[10px] text-[color:var(--ink-3)] mt-2">
                  {form.country_permissions.length === 0
                    ? "No restriction — user sees all countries"
                    : `${form.country_permissions.length} of ${allCountries.length} countries selected`}
                </div>
              </div>
            )}

            {/* Hub permissions - not applicable to admin */}
            {form.role !== "admin" && (
              <div className="border-t border-[color:var(--border)] pt-4">
                <div className="mono text-[10px] uppercase tracking-widest block mb-2">
                  Hub Access <span className="normal-case text-[color:var(--ink-3)]">(leave empty to grant every hub within the assigned countries)</span>
                </div>
                <div className="grid grid-cols-3 gap-2" data-testid="hub-perms">
                  {HUB_OPTIONS.map((c) => {
                    const checked = form.hub_permissions.includes(c);
                    return (
                      <label
                        key={c}
                        className={`flex items-center gap-2 px-3 py-2 cursor-pointer text-sm border ${checked ? "border-[color:var(--ink)] bg-[color:var(--surface-2)]" : "border-[color:var(--border)] hover:border-[color:var(--border-strong)]"}`}
                        data-testid={`hub-perm-${c}`}
                      >
                        <input
                          type="checkbox"
                          className="accent-[color:var(--ink)]"
                          checked={checked}
                          onChange={() => toggleHub(c)}
                        />
                        <span className="mono text-xs font-semibold">{c}</span>
                      </label>
                    );
                  })}
                </div>
                <div className="mono text-[10px] text-[color:var(--ink-3)] mt-2">
                  {form.hub_permissions.length === 0
                    ? "No hub restriction — user sees NILDAR, HILDAR and WILDAR"
                    : `Restricted to: ${form.hub_permissions.join(", ")}`}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <button className="btn-ghost text-sm" onClick={() => setOpen(false)}>Cancel</button>
            <button className="btn-primary text-sm" onClick={save} disabled={saving} data-testid="user-form-submit">
              {saving ? "Saving…" : editing ? "Update" : "Create"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CSV bulk import dialog */}
      <Dialog open={csvOpen} onOpenChange={setCsvOpen}>
        <DialogContent data-testid="csv-dialog" className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="serif text-3xl">Bulk Import Users</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-[color:var(--ink-2)]">
              Upload a CSV with columns: <span className="mono text-xs">name, email, password, role, menu_permissions, country_permissions, can_edit</span>.
              Use <span className="mono text-xs">;</span> to separate multiple menu keys or countries. Download the template above for a starting point.
            </p>
            <label className="block">
              <input
                type="file"
                accept=".csv"
                onChange={(e) => { setCsvFile(e.target.files?.[0] || null); setCsvResult(null); }}
                className="hidden"
                id="csv-file-input"
                data-testid="csv-file-input"
              />
              <div className="border-2 border-dashed border-[color:var(--border-strong)] hover:border-[color:var(--ink)] p-6 text-center cursor-pointer">
                <FileText size={22} className="mx-auto mb-2 text-[color:var(--ink-3)]" />
                <div className="mono text-xs text-[color:var(--ink-2)]">
                  {csvFile ? csvFile.name : "Click to choose users.csv"}
                </div>
              </div>
            </label>
            <div className="flex gap-2">
              <label htmlFor="csv-file-input" className="btn-ghost text-sm flex-1 text-center cursor-pointer">
                {csvFile ? "Change file" : "Choose file"}
              </label>
              <button
                className="btn-primary text-sm flex-1 flex items-center gap-2 justify-center"
                disabled={!csvFile || csvUploading}
                data-testid="csv-upload-btn"
                onClick={async () => {
                  if (!csvFile) return;
                  setCsvUploading(true);
                  setCsvResult(null);
                  try {
                    const fd = new FormData();
                    fd.append("file", csvFile);
                    const res = await api.post("/users/bulk-import", fd, { headers: { "Content-Type": "multipart/form-data" } });
                    setCsvResult(res.data);
                    toast.success(`Imported ${res.data.created + res.data.updated} users`);
                    await load();
                  } catch (err) {
                    toast.error(formatApiError(err.response?.data?.detail) || "Failed");
                  } finally { setCsvUploading(false); }
                }}
              >
                <Upload size={14} /> {csvUploading ? "Importing…" : "Import"}
              </button>
            </div>
            {csvResult && (
              <div className="border border-[color:var(--border)] p-4 space-y-2" data-testid="csv-result">
                <div className="flex gap-6 mono text-sm">
                  <span><span className="text-[color:var(--on-track)] font-semibold">{csvResult.created}</span> created</span>
                  <span><span className="text-[color:var(--amber)] font-semibold">{csvResult.updated}</span> updated</span>
                  <span><span className="text-[color:var(--behind)] font-semibold">{(csvResult.errors || []).length}</span> errors</span>
                </div>
                {(csvResult.errors || []).length > 0 && (
                  <div className="mono text-[10px] text-[color:var(--behind)] max-h-32 overflow-y-auto">
                    {csvResult.errors.map((e, i) => (
                      <div key={i}>Row {e.row}: {e.email || "-"} — {e.error}</div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <button className="btn-ghost text-sm" onClick={() => setCsvOpen(false)}>Close</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
